import { Link } from '@tanstack/react-router';

import { Checkbox } from '~/components/ui/checkbox';
import { useTranslationLocales } from '~/hooks/useTranslationLocales';

import type { LanguageApprovalOption } from '../../hooks/use-language-approval';

interface LanguageApprovalSectionProps {
  options: LanguageApprovalOption[];
  selected: string[];
  onToggle: (locale: string) => void;
  disabled?: boolean;
  /** The tenant's GMC connection, linked from a language it doesn't deliver yet. */
  connectionId?: string | undefined;
}

/**
 * "Which languages ship" on the delivery approval. English always ships; each translated locale
 * is delivered only when ticked here. Shows the QA counts the decision should rest on. A language
 * the GMC connection doesn't map is shown but can't be ticked — delivery would skip it — with a
 * link to set it up. Renders nothing when the run has no translations.
 */
export function LanguageApprovalSection({
  options,
  selected,
  onToggle,
  disabled = false,
  connectionId,
}: LanguageApprovalSectionProps) {
  const locales = useTranslationLocales(options.length > 0);
  if (options.length === 0) return null;
  const nameOf = (code: string) =>
    locales.data?.find((l) => l.code.toLowerCase() === code.toLowerCase())?.displayName ?? code;

  return (
    <section className="space-y-2 border-t px-6 py-5" data-testid="translations-approval-section">
      <div>
        <span
          className="text-foreground block text-sm font-semibold"
          data-testid="translations-approval-title"
        >
          Languages to deliver
        </span>
        <span className="text-muted-foreground block text-xs">
          English (en-US) always ships. Each ticked language is delivered to its own feed after it;
          products held back by QA are never delivered.
        </span>
      </div>
      {options.map((o) => {
        const id = `approve-locale-${o.locale}`;
        const deliverable = o.pass + o.flag;
        const locked = disabled || deliverable === 0 || !o.onConnection;
        return (
          <div
            key={o.locale}
            className="flex items-start gap-3"
            data-testid={`translations-approval-item-${o.locale}`}
          >
            <Checkbox
              id={id}
              data-testid={`translations-approval-checkbox-${o.locale}`}
              checked={selected.includes(o.locale)}
              disabled={locked}
              className="mt-0.5 cursor-pointer disabled:cursor-not-allowed"
              onCheckedChange={() => {
                onToggle(o.locale);
              }}
            />
            <label
              htmlFor={id}
              className={`block select-none ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className="text-foreground block text-sm">
                {nameOf(o.locale)} <span className="text-muted-foreground text-xs">{o.locale}</span>
              </span>
              <span
                className="text-muted-foreground block text-xs"
                data-testid={`translations-approval-item-${o.locale}-counts-value`}
              >
                {deliverable} deliverable ({o.pass} pass · {o.flag} flag)
                {o.heldBack > 0 ? ` · ${String(o.heldBack)} held back` : ''}
              </span>
              {!o.onConnection && (
                <span
                  className="text-muted-foreground block text-xs"
                  data-testid={`translations-approval-item-${o.locale}-not-connected-banner`}
                >
                  Not set up for delivery on the Google Merchant Center connection.
                  {connectionId && (
                    <>
                      {' '}
                      <Link
                        to="/onboarding/integration/google/google-merchant-center"
                        search={{ connectionId }}
                        data-testid={`translations-approval-item-${o.locale}-setup-link`}
                        className="text-primary cursor-pointer underline-offset-2 hover:underline"
                      >
                        Set it up
                      </Link>
                    </>
                  )}
                </span>
              )}
            </label>
          </div>
        );
      })}
    </section>
  );
}
